import { positionKey, Position, Direction } from '../core/GridGeometry.js';
import { GridHitDetector } from '../core/GridHitDetector.js';
import { getCanvasCoordinates } from '../input/canvasCoordinates.js';
import { KeyboardControllable, TraceStep, TraceDirResult } from '../input/KeyboardController.js';
import { VortexHub } from './VortexHub.js';
import { VortexHubRenderer } from './VortexHubRenderer.js';
import { GatePairUsage } from './VortexGateUsage.js';

export interface PendingDelete {
    clickedGateKey: string;
    pairedGateKey: string;
    usage: GatePairUsage;
}

const EMPTY_USAGE: GatePairUsage = {
    gamesUsing: [],
    gamesUsingInBest: [],
    affectedCount: 0,
    invalidatedWins: 0,
};

export interface VortexHubInputOptions {
    /** Look up gate pair usage from the App. Returns null if pair is unused. */
    getGatePairUsage?: (gateKey: string) => GatePairUsage | null;
    /** Called when a gate pair deletion is confirmed (App handles backup + removal). */
    onDeleteConfirmed?: (pending: PendingDelete) => void;
    /**
     * Called when, with delete-mode armed (Delete Gates clicked before selecting),
     * the user clicks a gate — the App moves it into delete-confirm.
     */
    onDeleteArmedGatePicked?: (pending: PendingDelete) => void;
}

/**
 * Input handler for the Vortex Hub (single "Links mode").
 *
 * Left-click:
 *  - on a gate → select its pair and enter build mode toward its fixed partner
 *    (destroying any existing link); while building: click partner to finish,
 *    click start gate to cancel, click second-to-last cell to backtrack, click
 *    aligned empty cell to extend.
 *  - on an empty cell → start a new pair (first gate pending); a second empty
 *    cell commits the pair. Re-clicking the first cell cancels.
 *
 * Right-click:
 *  - while building / pending-create → cancel.
 *  - on a gate (idle) → enter delete-confirm for that pair; a second right-click
 *    on the same pair confirms deletion.
 */
/** One-cell step per direction, for vetting a trace move before the cursor takes it. */
const STEP: Record<Direction, { dr: number; dc: number }> = {
    N: { dr: -1, dc: 0 }, S: { dr: 1, dc: 0 }, E: { dr: 0, dc: 1 }, W: { dr: 0, dc: -1 },
};

export class VortexHubInput implements KeyboardControllable {
    private hub: VortexHub;
    private canvas: HTMLCanvasElement;
    private detector: GridHitDetector;
    private onUpdate: () => void;

    private pendingCreate: Position | null = null;
    private pendingDelete: PendingDelete | null = null;
    /** Set while the App's Delete Gates button is in its confirm ("Sure?") state. */
    private deleteConfirmPending: PendingDelete | null = null;
    /** Set while Delete Gates is armed (clicked before selecting a gate): the next
     *  gate click picks that pair for delete-confirm rather than building. */
    private deleteArmed = false;

    private getGatePairUsage: ((gateKey: string) => GatePairUsage | null) | null;
    private onDeleteConfirmed: ((pending: PendingDelete) => void) | null;
    private onDeleteArmedGatePicked: ((pending: PendingDelete) => void) | null;

    constructor(
        hub: VortexHub,
        renderer: VortexHubRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: () => void,
        options?: VortexHubInputOptions
    ) {
        this.hub = hub;
        this.canvas = canvas;
        this.onUpdate = onUpdate;
        this.getGatePairUsage = options?.getGatePairUsage ?? null;
        this.onDeleteConfirmed = options?.onDeleteConfirmed ?? null;
        this.onDeleteArmedGatePicked = options?.onDeleteArmedGatePicked ?? null;

        const config = renderer.getConfig();
        this.detector = new GridHitDetector({
            cellSize: config.cellSize,
            padding: config.padding,
            gridSize: hub.getGrid().size
        });

        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    getPendingCreate(): Position | null {
        return this.pendingCreate;
    }

    getPendingDelete(): PendingDelete | null {
        return this.pendingDelete;
    }

    clearPending(): void {
        this.pendingCreate = null;
        this.pendingDelete = null;
    }

    setDeleteConfirm(pending: PendingDelete | null): void {
        this.deleteConfirmPending = pending;
    }

    /** Arm/disarm delete mode (Delete Gates clicked with no pair selected). */
    setDeleteArmed(armed: boolean): void {
        this.deleteArmed = armed;
    }

    /**
     * During Delete Gates confirm: clicking either red gate confirms deletion;
     * any other cell cancels. Never builds.
     */
    private resolveDeleteConfirm(cell: Position): void {
        const pending = this.deleteConfirmPending!;
        const key = positionKey(cell);
        this.deleteConfirmPending = null;
        if (key === pending.clickedGateKey || key === pending.pairedGateKey) {
            this.onDeleteConfirmed?.(pending);
        } else {
            this.onUpdate();
        }
    }

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = getCanvasCoordinates(this.canvas, e);
        const cell = this.detector.detectCell(x, y);
        if (cell) this.actOnCell(cell);
    };

    /**
     * The left-click FSM, shared by the mouse and the keyboard: resolve a pending
     * delete, else build (extend / finish / backtrack / cancel), else pick a gate to
     * build from or an empty cell to start a pair. Refreshes the view itself.
     */
    actOnCell(cell: Position): void {
        // While the Delete Gates button is confirming, picking a red gate confirms;
        // anything else cancels (and never builds).
        if (this.deleteConfirmPending) {
            this.resolveDeleteConfirm(cell);
            return;
        }

        // Delete Gates armed (clicked before selecting): the next gate click picks
        // that pair for delete-confirm; clicking elsewhere just disarms.
        if (this.deleteArmed) {
            this.deleteArmed = false;
            if (this.hub.isGate(cell)) {
                const key = positionKey(cell);
                const pairedKey = this.hub.getGatePairings().get(key);
                if (pairedKey) {
                    const usage = this.getGatePairUsage?.(key) ?? EMPTY_USAGE;
                    this.onDeleteArmedGatePicked?.({ clickedGateKey: key, pairedGateKey: pairedKey, usage });
                    return;
                }
            }
            this.onUpdate();
            return;
        }

        // A left-click always dismisses a right-click delete-confirm
        if (this.pendingDelete) {
            this.pendingDelete = null;
            this.onUpdate();
            return;
        }

        if (this.hub.isBuilding()) {
            this.handleBuildClick(cell);
            return;
        }

        if (this.pendingCreate) {
            this.handlePendingCreateClick(cell);
            return;
        }

        // Idle: gate → select + build; empty cell → start a new pair
        if (this.hub.isGate(cell)) {
            if (this.hub.startBuildFromGate(cell)) this.onUpdate();
        } else if (this.hub.canPlaceGate(cell)) {
            this.pendingCreate = { ...cell };
            this.onUpdate();
        }
    }

    private handleBuildClick(cell: Position): void {
        const buildPath = this.hub.getBuildPath();
        const key = positionKey(cell);

        if (key === positionKey(buildPath[0])) {
            // Click start gate → cancel (pair stays paired-but-unlinked)
            this.hub.cancelBuild();
            this.onUpdate();
        } else if (buildPath.length >= 2 && key === positionKey(buildPath[buildPath.length - 2])) {
            // Click second-to-last cell → backtrack
            if (this.hub.backtrackBuild()) this.onUpdate();
        } else if (this.hub.isGate(cell)) {
            // Click a gate → finish (only succeeds on the fixed partner)
            if (this.hub.finishBuild(cell)) this.onUpdate();
        } else {
            // Extend the path toward an aligned empty cell
            if (this.hub.extendBuild(cell)) this.onUpdate();
        }
    }

    private handlePendingCreateClick(cell: Position): void {
        const first = this.pendingCreate!;
        if (positionKey(cell) === positionKey(first)) {
            // Re-click the first cell → cancel
            this.pendingCreate = null;
            this.onUpdate();
            return;
        }
        if (this.hub.isGate(cell)) {
            // Clicked an existing gate → abandon creation, select that pair instead
            this.pendingCreate = null;
            if (this.hub.startBuildFromGate(cell)) this.onUpdate();
            else this.onUpdate();
            return;
        }
        // Second empty cell → commit the pair
        this.pendingCreate = null;
        this.hub.createPair(first, cell);
        this.onUpdate();
    }

    private handleRightClick = (e: MouseEvent): void => {
        e.preventDefault();
        const { x, y } = getCanvasCoordinates(this.canvas, e);
        const cell = this.detector.detectCell(x, y);
        if (cell) this.removeAtCell(cell);
    };

    /**
     * The right-click FSM, shared by the mouse and the keyboard (`C`): cancel a build
     * or a pending pair, else arm — and on a second press confirm — deleting the pair
     * under the cursor. Refreshes the view itself.
     */
    removeAtCell(cell: Position): void {
        // While the Delete Gates button is confirming, picking a red gate confirms;
        // anything else cancels.
        if (this.deleteConfirmPending) {
            this.resolveDeleteConfirm(cell);
            return;
        }

        if (this.hub.isBuilding()) {
            this.hub.cancelBuild();
            this.onUpdate();
            return;
        }

        if (this.pendingCreate) {
            this.pendingCreate = null;
            this.onUpdate();
            return;
        }

        if (this.pendingDelete) {
            const key = positionKey(cell);
            if (key === this.pendingDelete.clickedGateKey || key === this.pendingDelete.pairedGateKey) {
                // Second right-click on the same pair → confirm deletion
                const pending = this.pendingDelete;
                this.pendingDelete = null;
                this.onDeleteConfirmed?.(pending);
            } else {
                this.pendingDelete = null;
                this.onUpdate();
            }
            return;
        }

        // Idle right-click on a gate → enter delete-confirm
        if (this.hub.isGate(cell)) {
            const key = positionKey(cell);
            const pairedKey = this.hub.getGatePairings().get(key);
            if (!pairedKey) return;
            const usage = this.getGatePairUsage?.(key) ?? EMPTY_USAGE;
            this.pendingDelete = { clickedGateKey: key, pairedGateKey: pairedKey, usage };
            this.onUpdate();
        }
    }

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Taps run the same two FSMs the mouse does. The trace is where linking gets
    // quick: hold space on a gate and steer to its partner to lay the path, exactly
    // as Antennas draws an antenna — `startBuildFromGate` / `extendBuild` /
    // `backtrackBuild` / `finishBuild` line up one-to-one with the gesture.

    placeAtCursor(pos: Position): boolean {
        this.actOnCell(pos);
        return false; // the FSM refreshes the Hub itself
    }

    deleteAtCursor(pos: Position): boolean {
        this.removeAtCell(pos);
        return false;
    }

    /** A trace only ever builds a link, and links start at a gate — beginning one
     *  anywhere else does nothing (pair creation stays a tap). */
    traceStart(pos: Position): void {
        if (this.hub.isBuilding() || this.pendingDelete || this.deleteConfirmPending) return;
        if (this.hub.isGate(pos)) this.hub.startBuildFromGate(pos);
    }

    /**
     * The build has reached the partner: its gate is the last cell of the path, so
     * the whole link (including the final segment) is drawn. The commit to the Hub
     * still waits for release, so the trace can be backed off first.
     */
    private reachedPartner(): boolean {
        const path = this.hub.getBuildPath();
        return path.length >= 2 && this.hub.isGate(path[path.length - 1]);
    }

    /**
     * Each step lays one more cell of the path, or backtracks by stepping onto the
     * cell before the end. Reaching the partner gate extends the path *onto* it — so
     * the last segment shows — but doesn't link yet: the commit waits for release
     * (like Antennas' emitter), and a step back off it resumes building.
     */
    traceStep({ to }: TraceStep): boolean {
        if (!this.hub.isBuilding()) return false;
        const path = this.hub.getBuildPath();

        if (path.length >= 2 && positionKey(to) === positionKey(path[path.length - 2])) {
            if (this.hub.backtrackBuild()) this.onUpdate();
            return false;
        }
        if (this.reachedPartner()) return false; // pinned; only backtrack, handled above
        if (positionKey(to) === this.hub.getBuildPartnerKey()) {
            if (this.hub.extendBuildToPartner(to)) this.onUpdate();
            return false;
        }
        if (this.hub.extendBuild(to)) this.onUpdate();
        return false;
    }

    /** Releasing on the partner gate commits the link; anywhere else the build stays
     *  open, so you can carry on with another trace or with taps. */
    traceEnd(): boolean {
        if (this.reachedPartner() && this.hub.commitBuildToPartner()) this.onUpdate();
        return false; // the Hub refreshes through onUpdate itself
    }

    /**
     * Keep the cursor on the path being built: a step onto a digit, a gate that isn't
     * the partner, or a cell another link already uses is refused. Backtracking onto
     * our own previous cell is always allowed. Once the partner is reached, everything
     * but the step back off it is refused — the trace can't run on past the end.
     */
    traceDirPress(pos: Position, dir: Direction): TraceDirResult {
        if (!this.hub.isBuilding()) return 'move';

        const d = STEP[dir];
        const to: Position = { row: pos.row + d.dr, col: pos.col + d.dc };
        const path = this.hub.getBuildPath();
        const key = positionKey(to);

        if (path.length >= 2 && key === positionKey(path[path.length - 2])) return 'move'; // backtrack
        if (this.reachedPartner()) return 'blocked'; // pinned on the partner; only backtrack (above)
        if (key === positionKey(path[0])) return 'blocked';        // the start gate cancels; don't wander onto it
        if (path.some(p => positionKey(p) === key)) return 'blocked';
        if (this.hub.isGate(to)) {
            // Only the fixed partner ends the path.
            return key === this.hub.getBuildPartnerKey() ? 'move' : 'blocked';
        }
        return this.hub.canBuildThrough(to) ? 'move' : 'blocked';
    }

    /** `X` goes to the first gate pair — the network you're wiring up. */
    cursorOrigin(): Position | null {
        for (const key of this.hub.getGates()) {
            const [row, col] = key.split(',').map(Number);
            return { row, col };
        }
        return null;
    }

    destroy(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }
}
